// SentenceBuilder: word tiles in a tray and an answer row. Tap, click, Enter or Space places a tile; tapping a placed
// tile returns it; ←/→ on a placed tile moves it (focus stays on the moved tile). On desktop (mouse or pen) tiles can
// also be dragged to place and reorder them. Every change is announced in a polite live region for screen readers.
// The server grades the sentence by grammar, so any correct word order is accepted.

import type { FC, PointerEvent as ReactPointerEvent, ReactNode } from 'react'

import { Fragment, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { motion } from 'motion/react'
import { cn } from '@/lib/utils'
import { Bi } from '@/prakrit/components/common/bi'

export interface BuilderTile {
    id: string
    text: string
}

interface DragState {
    id: string
    from: 'tray' | 'row'
    pointerId: number
    startX: number
    startY: number
    offsetX: number
    offsetY: number
    width: number
    height: number
    active: boolean
}

interface Ghost {
    id: string
    x: number
    y: number
    width: number
    height: number
}

interface SentenceBuilderProps {
    tiles: BuilderTile[]
    /** How many words the sentence needs (drawn as empty slots while the row is not full). */
    slots: number
    /** Placed tile ids, in order. */
    value: string[]
    onChange: (ids: string[]) => void
    disabled?: boolean
    status?: 'correct' | 'wrong' | null
    uid: string
}

const inside = (el: HTMLElement | null, x: number, y: number): boolean => {
    if (!el) return false

    const r = el.getBoundingClientRect()

    return x >= r.left - 12 && x <= r.right + 12 && y >= r.top - 12 && y <= r.bottom + 12
}

export const SentenceBuilder: FC<SentenceBuilderProps> = ({
    tiles,
    slots,
    value,
    onChange,
    disabled = false,
    status = null,
    uid
}): ReactNode => {
    const rowRef = useRef<HTMLDivElement>(null)
    const trayRef = useRef<HTMLDivElement>(null)
    const drag = useRef<DragState | null>(null)
    const suppressClick = useRef(false)
    const [ghost, setGhost] = useState<Ghost | null>(null)
    const [insertAt, setInsertAt] = useState<number | null>(null)
    // What the last tap, key or drop did, for screen readers (the region is always in the page, so changes are read).
    const [said, setSaid] = useState('')
    const byId = new Map(tiles.map(t => [t.id, t]))
    const placed = value.filter(id => byId.has(id))
    const used = new Set(placed)
    const draggingId = ghost?.id ?? null

    const focusLater = (selector: string) => {
        window.requestAnimationFrame(() => {
            const root = rowRef.current?.closest<HTMLElement>('[data-builder]')
            const el = root?.querySelector<HTMLElement>(selector)

            el?.focus()
        })
    }

    const textOf = (id: string): string => byId.get(id)?.text ?? ''

    const place = (id: string) => {
        if (disabled || used.has(id)) return

        const remaining = tiles.filter(t => !used.has(t.id) && t.id !== id)
        const after = remaining.find(t => tiles.indexOf(t) > tiles.findIndex(x => x.id === id)) ?? remaining[0]
        const full = placed.length + 1 >= slots

        onChange([...placed, id])
        setSaid(`${textOf(id)}: वाक्य में ${placed.length + 1}/${slots} · placed, word ${placed.length + 1} of ${slots}`)

        // Once the row has every word it needs, focus the row itself so Enter checks (instead of moving a tile).
        if (full || !after) window.requestAnimationFrame(() => rowRef.current?.focus())
        else focusLater(`[data-tray-tile="${after.id}"]`)
    }

    const remove = (id: string) => {
        if (disabled) return

        const index = placed.indexOf(id)
        const rest = placed.filter(x => x !== id)
        const neighbour = rest[index] ?? rest[index - 1]

        onChange(rest)
        setSaid(`${textOf(id)}: वापस हटाया · removed`)
        focusLater(neighbour ? `[data-row-tile="${neighbour}"]` : `[data-tray-tile="${id}"]`)
    }

    const move = (id: string, delta: number) => {
        if (disabled) return

        const index = placed.indexOf(id)
        const target = index + delta

        if (index < 0 || target < 0 || target >= placed.length) return

        const next = [...placed]

        next.splice(index, 1)
        next.splice(target, 0, id)
        onChange(next)
        setSaid(`${textOf(id)}: जगह ${target + 1} · moved to word ${target + 1} of ${placed.length}`)
        // Reordering moves the button in the page, which can drop keyboard focus: put it back on the moved tile.
        focusLater(`[data-row-tile="${id}"]`)
    }

    /** Where a tile dropped at (x, y) goes, counted among the placed tiles other than the dragged one. */
    const indexAt = (x: number, y: number, exclude: string): number => {
        const els = Array.from(rowRef.current?.querySelectorAll<HTMLElement>('[data-row-tile]') ?? []).filter(el => el.dataset.rowTile !== exclude)

        for (let i = 0; i < els.length; i += 1) {
            const r = els[i].getBoundingClientRect()

            if (y < r.top) return i
            if (y <= r.bottom && x < r.left + r.width / 2) return i
        }

        return els.length
    }

    const onPointerDown = (event: ReactPointerEvent<HTMLButtonElement>, id: string, from: 'tray' | 'row') => {
        suppressClick.current = false

        if (disabled || event.button !== 0 || event.pointerType === 'touch') return

        const rect = event.currentTarget.getBoundingClientRect()

        drag.current = {
            id,
            from,
            pointerId: event.pointerId,
            startX: event.clientX,
            startY: event.clientY,
            offsetX: event.clientX - rect.left,
            offsetY: event.clientY - rect.top,
            width: rect.width,
            height: rect.height,
            active: false
        }

        event.currentTarget.setPointerCapture(event.pointerId)
    }

    const onPointerMove = (event: ReactPointerEvent<HTMLButtonElement>) => {
        const d = drag.current

        if (!d || d.pointerId !== event.pointerId) return

        if (!d.active) {
            if (Math.hypot(event.clientX - d.startX, event.clientY - d.startY) < 6) return

            d.active = true
        }

        setGhost({ id: d.id, x: event.clientX - d.offsetX, y: event.clientY - d.offsetY, width: d.width, height: d.height })
        setInsertAt(inside(rowRef.current, event.clientX, event.clientY) ? indexAt(event.clientX, event.clientY, d.id) : null)
    }

    const endDrag = (event: ReactPointerEvent<HTMLButtonElement>, cancelled: boolean) => {
        const d = drag.current

        if (!d || d.pointerId !== event.pointerId) return

        drag.current = null

        if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId)

        if (!d.active) return

        // The click that follows this pointerup belongs to the drag; reset right after so later taps still count.
        suppressClick.current = true
        window.setTimeout(() => {
            suppressClick.current = false
        }, 0)
        setGhost(null)
        setInsertAt(null)

        if (cancelled) return

        if (inside(rowRef.current, event.clientX, event.clientY)) {
            const at = indexAt(event.clientX, event.clientY, d.id)
            const rest = placed.filter(x => x !== d.id)

            rest.splice(at, 0, d.id)
            onChange(rest)
            setSaid(`${textOf(d.id)}: जगह ${at + 1} · word ${at + 1} of ${rest.length}`)
        }
        else if (d.from === 'row' && inside(trayRef.current, event.clientX, event.clientY)) {
            onChange(placed.filter(x => x !== d.id))
            setSaid(`${textOf(d.id)}: वापस हटाया · removed`)
        }
    }

    const onClick = (id: string, from: 'tray' | 'row') => {
        if (suppressClick.current) {
            suppressClick.current = false

            return
        }

        if (from === 'tray') place(id)
        else remove(id)
    }

    const tileClass = (extra?: string) => cn(
        'relative inline-flex min-h-tap min-w-tap max-w-full select-none items-center justify-center break-words rounded-xl border-2 border-b-4 bg-card px-4 py-1.5 shadow-sm transition-colors',
        'border-border hover:border-saffron/70 focus-visible:border-saffron',
        disabled && 'cursor-default hover:border-border',
        extra
    )

    const caret = <span aria-hidden='true' className='h-10 w-1 rounded-full bg-saffron' />
    const rest = placed.filter(id => id !== draggingId)
    const emptySlots = Math.max(0, slots - placed.length)

    return (
        <div data-builder className='flex flex-col gap-5'>
            <div
                ref={rowRef}
                data-tile-row
                role='group'
                tabIndex={-1}
                aria-label='आपका वाक्य · Your sentence'
                className={cn(
                    'flex min-h-[5.25rem] flex-wrap items-center gap-2 rounded-2xl border-2 border-dashed p-3 outline-none transition-colors focus-visible:ring-3 focus-visible:ring-ring',
                    status === 'correct' ? 'border-success bg-success-soft' : status === 'wrong' ? 'animate-shake-x border-danger bg-danger-soft' : 'border-border bg-surface-muted/60',
                    insertAt !== null && 'border-saffron bg-saffron/5'
                )}
            >
                {placed.map((id, i) => {
                    const tile = byId.get(id) as BuilderTile
                    const isDragged = id === draggingId
                    const restIndex = rest.indexOf(id)

                    return (
                        <Fragment key={id}>
                            {!isDragged && insertAt !== null && insertAt === restIndex && caret}
                            <motion.button
                                layout
                                layoutId={`${uid}-tile-${id}`}
                                transition={{ duration: 0.22, ease: 'easeOut' }}
                                type='button'
                                data-row-tile={id}
                                disabled={disabled}
                                aria-label={`${tile.text} (${i + 1}/${placed.length}) — वापस हटाएँ · remove. ← → से जगह बदलें · move with arrow keys`}
                                onClick={() => onClick(id, 'row')}
                                onKeyDown={event => {
                                    if (event.key === 'ArrowLeft') {
                                        event.preventDefault()
                                        move(id, -1)
                                    }
                                    else if (event.key === 'ArrowRight') {
                                        event.preventDefault()
                                        move(id, 1)
                                    }
                                }}
                                onPointerDown={event => onPointerDown(event, id, 'row')}
                                onPointerMove={onPointerMove}
                                onPointerUp={event => endDrag(event, false)}
                                onPointerCancel={event => endDrag(event, true)}
                                className={tileClass(cn(
                                    status === 'correct' && 'border-success',
                                    status === 'wrong' && 'border-danger/60',
                                    isDragged && 'opacity-40'
                                ))}
                            >
                                <span lang='pra' className='pra pra-lg'>{tile.text}</span>
                            </motion.button>
                        </Fragment>
                    )
                })}
                {insertAt !== null && insertAt >= rest.length && caret}

                {placed.length === 0 && (
                    <p className='px-1 text-sm text-muted-foreground'>
                        <span lang='hi' data-ex='hi' className='hindi'>नीचे के शब्दों को छूकर यहाँ वाक्य बनाइए</span>
                        <span aria-hidden='true' className='ex-sep'> · </span>
                        <span lang='en' data-ex='en'>Tap the words below to build the sentence here</span>
                    </p>
                )}

                {emptySlots > 0 && placed.length > 0 && Array.from({ length: emptySlots }, (_, i) => (
                    <span key={`slot-${i}`} aria-hidden='true' className='inline-flex h-12 w-16 rounded-xl border-2 border-dashed border-border' />
                ))}
            </div>

            {/* Shown only while a placed word has keyboard focus (globals.css, .tile-keys-hint). */}
            <p aria-hidden='true' className='tile-keys-hint -mt-3 px-1 text-sm text-muted-foreground'>
                <Bi hi='← → से शब्द की जगह बदलें, Enter से वापस हटाएँ' en='← → moves the word, Enter takes it back' layout='inline' />
            </p>

            <div ref={trayRef} role='group' aria-label='शब्द · Word tiles' className='flex min-h-16 flex-wrap justify-center gap-2.5'>
                {tiles.map(tile => {
                    if (used.has(tile.id)) {
                        return (
                            <span key={tile.id} aria-hidden='true' className='inline-flex min-h-tap items-center justify-center rounded-xl border-2 border-dashed border-border bg-surface-muted/50 px-4 py-1.5'>
                                <span lang='pra' className='pra pra-lg invisible'>{tile.text}</span>
                            </span>
                        )
                    }

                    return (
                        <motion.button
                            key={tile.id}
                            layout
                            layoutId={`${uid}-tile-${tile.id}`}
                            transition={{ duration: 0.22, ease: 'easeOut' }}
                            type='button'
                            data-tray-tile={tile.id}
                            disabled={disabled}
                            aria-label={`${tile.text} — वाक्य में रखें · place in the sentence`}
                            onClick={() => onClick(tile.id, 'tray')}
                            onPointerDown={event => onPointerDown(event, tile.id, 'tray')}
                            onPointerMove={onPointerMove}
                            onPointerUp={event => endDrag(event, false)}
                            onPointerCancel={event => endDrag(event, true)}
                            className={tileClass(cn(tile.id === draggingId && 'opacity-40', !disabled && 'cursor-grab active:cursor-grabbing'))}
                        >
                            <span lang='pra' className='pra pra-lg'>{tile.text}</span>
                        </motion.button>
                    )
                })}
            </div>

            <p aria-live='polite' className='sr-only'>{said}</p>

            {/* The drag ghost exists only while a mouse or pen drag is under way (never on the server). */}
            {ghost && createPortal(
                <div
                    aria-hidden='true'
                    className='pointer-events-none fixed left-0 top-0 z-[60] inline-flex items-center justify-center rounded-xl border-2 border-b-4 border-saffron bg-card px-4 py-1.5 shadow-lg'
                    style={{ transform: `translate(${ghost.x}px, ${ghost.y}px) rotate(2deg)`, width: ghost.width, height: ghost.height }}
                >
                    <span lang='pra' className='pra pra-lg'>{byId.get(ghost.id)?.text}</span>
                </div>,
                document.body
            )}
        </div>
    )
}
